import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { getApiErrorMessage, rankingsApi } from "../api";
import type { Mypage } from "../api/rankings";
import { useGuardedAsyncAction } from "../hooks/useGuardedAsyncAction";

interface MypageContextValue {
  mypage: Mypage | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastFetchedAt: number | null;
}

const MypageContext = createContext<MypageContextValue | null>(null);

export const MypageProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [mypage, setMypage] = React.useState<Mypage | null>(null);
  const { isLoading, error, lastFetchedAt, run, setLastFetchedAt, setError } =
    useGuardedAsyncAction();

  const fetchMypage = useCallback(async () => {
    await run(
      async () => {
        const data = await rankingsApi.getMyPage();
        setMypage(data);
      },
      { onErrorMessage: getApiErrorMessage },
    );
  }, [run]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMypage();
    } else {
      setMypage(null);
      setLastFetchedAt(null);
      setError(null);
    }
  }, [fetchMypage, isAuthenticated, setError, setLastFetchedAt]);

  return (
    <MypageContext.Provider
      value={{
        mypage,
        isLoading,
        error,
        refresh: fetchMypage,
        lastFetchedAt,
      }}
    >
      {children}
    </MypageContext.Provider>
  );
};

export const useMypageContext = (): MypageContextValue => {
  const ctx = useContext(MypageContext);
  if (!ctx)
    throw new Error("useMypageContext must be used within MypageProvider");
  return ctx;
};
